import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { makeSelectWhoami } from '../containers/Auth_old/selectors';
import { fetchAndStoreWxJwt } from '../utils/wxJwtToken';

export const WX_JWT_PENDING = 'pending';
export const WX_JWT_READY = 'ready';
export const WX_JWT_UNAVAILABLE = 'unavailable';

/**
 * Hook that handles JWT fetching and returns its status.
 * For unified auth accounts: fetches the JWT, then reports ready or
 * unavailable. For non-unified auth accounts: ready at once (no JWT needed).
 *
 * @returns {'pending'|'ready'|'unavailable'}
 */
export function useWxJwtStatus() {
  const whoami = useSelector(makeSelectWhoami());
  const [status, setStatus] = useState(WX_JWT_PENDING);

  // Stable primitives derived from whoami — avoids cycling the effect on every
  // Redux dispatch that returns a new whoami object reference.
  const accountUnifiedAuth = whoami?.account_unified_auth ?? false;
  const accountSlugOrName =
    whoami?.account_slug || whoami?.account_name || null;

  useEffect(() => {
    if (!accountSlugOrName && !accountUnifiedAuth) {
      setStatus(WX_JWT_PENDING);
      return;
    }

    // No WX JWT needed for non-unified-auth accounts — proceed immediately.
    if (!accountUnifiedAuth) {
      setStatus(WX_JWT_READY);
      return;
    }

    // If unified auth is required, fetch JWT and wait for it to be ready
    let cancelled = false;

    const fetchAndWaitForJwt = async () => {
      try {
        // Fetch JWT - this will resolve the internal promise when done
        const success = await fetchAndStoreWxJwt(whoami);
        if (!cancelled) {
          setStatus(success ? WX_JWT_READY : WX_JWT_UNAVAILABLE);
        }
      } catch {
        // On error, JWT is not ready
        if (!cancelled) {
          setStatus(WX_JWT_UNAVAILABLE);
        }
      }
    };

    fetchAndWaitForJwt();

    // Cleanup function
    // eslint-disable-next-line consistent-return
    return () => {
      cancelled = true;
      setStatus(WX_JWT_PENDING);
    };
    // accountUnifiedAuth and accountSlugOrName are primitive values derived from
    // whoami — they only change when the actual account config changes, not on
    // every Redux reference update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountUnifiedAuth, accountSlugOrName]);

  return status;
}

/**
 * @returns {boolean} Whether JWT token is ready (or not needed)
 */
function useWxJwtReady() {
  return useWxJwtStatus() === WX_JWT_READY;
}

export default useWxJwtReady;
